import React, { useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { FormProvider, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient } from '@tanstack/react-query'
import { PaytrieAPI } from '@paytrie/client'

import { Button } from '@/components/Button/Button'
import { IntendedPurposeSelector } from '@/components/Select/IntendedPurposeSelector'
import { LoadingIcon } from '@/components/LoadingIcon'
import {
  ErrorBlockText,
  LeftAlignSmallerText,
} from '@/components/widget-components'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { usePageStateStore, useSignupStore } from '@/reducers'
import {
  trackOnboardingStepCompleted,
  trackOnboardingStepFailed,
  trackOnboardingStepSubmitted,
  trackOnboardingStepViewed,
} from '@/utils/analytics'
import { OccupationFields } from './OccupationFields'
import {
  buildOccupationSchema,
  CAPTURE_DOB_ROUTE,
  OCCUPATION_DEFAULTS,
  packOccupation,
  unpackOccupation,
  type OccupationFormValues,
} from './occupation'

const toDefaultValues = (
  user?: PaytrieAPI.CurrentUser
): OccupationFormValues =>
  user ? unpackOccupation(user.occupation ?? '') : OCCUPATION_DEFAULTS

const SignupOccupationInner = React.memo(function SignupOccupationInner({
  user,
}: {
  user?: PaytrieAPI.CurrentUser
}) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const purpose = useSignupStore.use.purpose()
  const goToNextState = useSignupStore.use.nextState()

  const profileBirthDay = user?.dob ?? ''

  const schema = useMemo(() => buildOccupationSchema(t), [t])
  const methods = useForm<OccupationFormValues>({
    resolver: zodResolver(schema),
    defaultValues: toDefaultValues(user),
    mode: 'onTouched',
  })
  const {
    handleSubmit,
    setError,
    clearErrors,
    formState: { errors, isValid, isSubmitting },
  } = methods

  useEffect(() => {
    trackOnboardingStepViewed('occupation_and_purpose')
  }, [])

  const onValid = async (values: OccupationFormValues) => {
    trackOnboardingStepSubmitted('occupation_and_purpose')
    clearErrors('root')

    if (!profileBirthDay) {
      trackOnboardingStepFailed('occupation_and_purpose', 'missing_dob')
      navigate(CAPTURE_DOB_ROUTE)
      return
    }

    const occupation = packOccupation(values)
    try {
      const res = await PaytrieAPI.updateCurrentUser({
        occupation,
        purpose: purpose || undefined,
        currentFlow: PaytrieAPI.UpdateCurrentUserBodyCurrentFlow.OCCUPATION,
      })

      if (res.status !== 200) {
        const isDuplicate = res.status === 409
        const message =
          'errors' in res.data && res.data.errors?.[0]?.message
            ? res.data.errors[0].message
            : ''
        setError('root', {
          message: isDuplicate
            ? t('occupation.error.duplicateUser')
            : message || t('occupation.error.unableToProcess'),
        })
        trackOnboardingStepFailed(
          'occupation_and_purpose',
          isDuplicate ? 'duplicate_user' : 'unable_to_process'
        )
        return
      }
    } catch {
      setError('root', { message: t('occupation.error.unableToProcess') })
      trackOnboardingStepFailed('occupation_and_purpose', 'unable_to_process')
      return
    }

    localStorage.setItem('purpose', purpose || '')

    await queryClient.invalidateQueries({
      queryKey: ['v2', 'users', 'me'],
    })
    goToNextState()
    trackOnboardingStepCompleted('occupation_and_purpose')

    navigate('/')
  }

  const onInvalid = () =>
    trackOnboardingStepFailed('occupation_and_purpose', 'validation_failed')

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={handleSubmit(onValid, onInvalid)}
        data-testid="form-occupation"
      >
        <div className="flex flex-col gap-2">
          <OccupationFields />

          <div
            className="text-base font-normal uppercase"
            data-testid="container-label"
          >
            {t('occupation.intendedPurpose.title')}
          </div>
          <IntendedPurposeSelector />

          <div className="" data-testid="container-attestation">
            <LeftAlignSmallerText>
              {t('occupation.attestation.part1')}
              <a
                href={import.meta.env.VITE_PAYTRIE_PEP_URL}
                target="_blank"
                style={{ textDecoration: 'underline' }}
                data-testid="container-link"
              >
                {t('occupation.attestation.pep.link')}
              </a>
              {t('occupation.attestation.part2')}
              <a
                href={import.meta.env.VITE_PAYTRIE_HIO_URL}
                target="_blank"
                style={{ textDecoration: 'underline' }}
                data-testid="container-link"
              >
                {t('occupation.attestation.hio.link')}
              </a>
              {t('occupation.attestation.part3')}
              <a
                href={import.meta.env.VITE_PAYTRIE_TPD_URL}
                target="_blank"
                style={{ textDecoration: 'underline' }}
                data-testid="container-party-link"
              >
                {t('occupation.attestation.thirdParty.link')}
              </a>
              {t('occupation.attestation.part4')}
            </LeftAlignSmallerText>
          </div>

          <Button
            className="inline-flex items-center py-8 px-4 w-full justify-center lg:gap-2"
            type="submit"
            disabled={!isValid || !purpose || isSubmitting}
            data-testid="btn-continue-submit"
          >
            {t('occupation.submit')}
          </Button>
          <ErrorBlockText data-testid="error-form-message">
            {errors.root?.message ?? ''}
          </ErrorBlockText>
        </div>
      </form>
    </FormProvider>
  )
})

export default function SignupOccupation() {
  const authToken = usePageStateStore.use.authToken() || ''

  const { data: loggedInUser, isLoading } = useCurrentUser(!!authToken)

  if (isLoading) {
    return (
      <div
        className="my-auto flex justify-center"
        data-testid="container-spinner"
      >
        <LoadingIcon />
      </div>
    )
  }

  return <SignupOccupationInner user={loggedInUser} />
}
